/**
 * CardStrip — the OEM Home bottom-strip horizontal scroller (Phase 2).
 *
 * Ports the reference HomeOEM bottom strip: a horizontally-scrolling row of
 * fixed-size cards floating over the map. Card order matches the gripp-os
 * reference exactly: Activity · Assets · Routines · Products · Customers ·
 * Campaigns.
 *
 * Three cards are map-plottable (Activity / Assets / Routines): selecting one
 * lifts its `HomeEntityType` to the page via `onSelectPlot`, switching the map's
 * plotting mode. Selecting the active card again clears it (back to Locations).
 * Products / Campaigns / Customers are display-only.
 */
import React from 'react';
import { HomeEntityType } from '@gripp/shared-logic';
import { ScrollView, StyleSheet, View } from 'react-native';
import { tokens } from '@gripp/shared-ui';
import { TimeRange } from '../types';
import { ActivityCard } from './activityCard';
import { AssetsCard } from './assetsCard';
import { RoutinesCard } from './routinesCard';
import { ProductsCard } from './productsCard';
import { CustomersCard } from './customersCard';
import { CampaignsCard } from './campaignsCard';

export interface CardStripProps {
  range: TimeRange;
  /** Active map plotting mode (null = resting Location view). */
  plotMode: HomeEntityType | null;
  /** Toggles a plotting mode (selecting the active one again clears it). */
  onSelectPlot: (entityType: HomeEntityType) => void;
}

/** Fixed-footprint wrapper for one card (design: w-[320px] h-[240px] snap-start). */
const StripCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.stripCard}>{children}</View>
);

export const CardStrip: React.FC<CardStripProps> = ({
  range,
  plotMode,
  onSelectPlot,
}) => {
  return (
    <>
      {/* Web-only: thin, tokenized horizontal scrollbar so mouse users can reach
          the off-screen cards (gripp-os hides it and relies on trackpad scroll,
          which strands mouse users on a desktop surface). slate-400 thumb via
          #RRGGBBAA hex-alpha (80 ≈ 0.5, CC ≈ 0.8). */}
      <style>{`
        #home-card-scroller {
          scrollbar-width: thin;
          scrollbar-color: ${tokens.colors.slate[400]}80 transparent;
        }
        #home-card-scroller::-webkit-scrollbar { height: 8px; }
        #home-card-scroller::-webkit-scrollbar-track { background: transparent; }
        #home-card-scroller::-webkit-scrollbar-thumb {
          background: ${tokens.colors.slate[400]}80;
          border-radius: 4px;
        }
        #home-card-scroller::-webkit-scrollbar-thumb:hover {
          background: ${tokens.colors.slate[400]}CC;
        }
      `}</style>
      <ScrollView
        horizontal
        nativeID="home-card-scroller"
        showsHorizontalScrollIndicator
        contentContainerStyle={styles.content}
        style={styles.scroller}
        testID="home-dashboard-cardstrip"
      >
        <StripCard>
        <ActivityCard
          range={range}
          selected={plotMode === HomeEntityType.Activity}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Activity)}
        />
      </StripCard>
      <StripCard>
        <AssetsCard
          range={range}
          selected={plotMode === HomeEntityType.Asset}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Asset)}
        />
      </StripCard>
      <StripCard>
        <RoutinesCard
          range={range}
          selected={plotMode === HomeEntityType.Routine}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Routine)}
        />
      </StripCard>
      <StripCard>
        <ProductsCard range={range} />
      </StripCard>
      <StripCard>
        <CustomersCard range={range} />
      </StripCard>
      <StripCard>
        <CampaignsCard range={range} />
      </StripCard>
      </ScrollView>
    </>
  );
};

CardStrip.displayName = 'CardStrip';

const styles = StyleSheet.create({
  scroller: {
    // The scroller is overlaid on the map; let it size to its content height.
    flexGrow: 0,
  },
  content: {
    flexDirection: 'row',
    gap: tokens.spacing.md, // gap-3 (12)
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  stripCard: {
    width: 320,
    height: 240,
  },
});
